import React, { useState } from 'react';
import { Button, CircularProgress, Divider, Typography, Box } from '@mui/material';
import { Dialog } from '@medely/web-components';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useFileUpload } from 'hooks/useFileUpload';
import useRequestCsv from 'hooks/useRequestCsv';
import config from 'config';

interface ImportSection {
  title: string;
  description?: string;
  downloadUrl: string;
  downloadFilename: string;
  uploadUrl: string;
  uploadAccept?: string;
  showCondition?: boolean;
}

interface ImportOptionsModalProps {
  sections: ImportSection[];
}

const ImportOptionsModal = ({ sections }: ImportOptionsModalProps) => {
  const [open, setOpen] = useState(false);
  const downloadCSV = useRequestCsv();

  // Filter sections based on their show conditions
  const visibleSections = sections.filter(section => section.showCondition);

  if (visibleSections.length === 0) {
    return null;
  }

  const handleDownload = (section: ImportSection) => {
    downloadCSV({
      url: section.downloadUrl,
      filename: section.downloadFilename,
    });
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        startIcon={<UploadIcon />}
      >
        Import Options
      </Button>
      
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import Options
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose from the available import options below. Download templates to see the required format.
          </Typography>

          {visibleSections.map((section, index) => (
            <ImportSection
              key={section.title}
              section={section}
              onDownload={() => handleDownload(section)}
              showDivider={index < visibleSections.length - 1}
            />
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

const ImportSection = ({ section, onDownload, showDivider }: { 
  section: ImportSection; 
  onDownload: () => void;
  showDivider: boolean;
}) => {
  const { hiddenInput, openFilePrompt, uploading } = useFileUpload({
    url: section.uploadUrl,
    accept: section.uploadAccept || 'text/csv',
  });

  return (
    <>
      <Box sx={{ mb: showDivider ? 3 : 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {section.title}
        </Typography>
        {section.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {section.description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download Template
          </Button>
          
          {hiddenInput}
          <Button
            size="small"
            variant="outlined"
            disabled={uploading}
            onClick={openFilePrompt}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Import CSV'}
          </Button>
        </Box>
      </Box>
      
      {showDivider && <Divider sx={{ my: 3 }} />}
    </>
  );
};

export default ImportOptionsModal;
